/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import {
  $createCodeNode,
  $isCodeNode,
  CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  CODE_LANGUAGE_MAP,
  getLanguageFriendlyName,
} from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  mergeRegister,
} from "@lexical/utils";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";

import { InsertImageDialog } from "./imagePlugin";
import { INSERT_YOUTUBE_COMMAND } from "./youtubePlugin";
import { FillURL, YoutubeUrlDialog } from "./youtubeToolbar";

const LowPriority = 1;

function Divider() {
  return <div className="divider" />;
}

export default function ToolbarPlugin({ setIsLinkEditMode }) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [activeEditor, setActiveEditor] = useState(false);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isheadNode, setheadNode] = useState(false);
  const [imageInput, setImageInput] = useState(false);
  const [youtubeInput, setYoutubeInput] = useState(false);

  const [blockType, setBlockType] = useState("");

  CODE_LANGUAGE_FRIENDLY_NAME_MAP.shell = "Shell";
  CODE_LANGUAGE_MAP.shell = "shell";
  console.log(CODE_LANGUAGE_MAP);
  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          // We split the first and last node by the selection
          // So that we don't format unselected text inside those nodes
          if ($isTextNode(node)) {
            // Use a separate variable to ensure TS does not lose the refinement
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }

            if (textNode.__style !== "") {
              textNode.setStyle("");
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat("");
            }
            node = textNode;
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat("");
          }
        });
      }
    });
  }, [activeEditor]);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });
      const type = $isHeadingNode(element)
        ? element.getTag()
        : element.getType();
      setBlockType(type);
      setheadNode($isHeadingNode(element));
      if ($isCodeNode(element)) {
        // setCodeLanguage(
        //   language ? CODE_LANGUAGE_MAP[language] || language : '',
        // );
        // return;
      }
      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
    }
  }, []);
  const headingTag = (tag) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType !== tag) {
          clearFormatting();
          $setBlocksType(selection, () => $createHeadingNode(tag));
        } else {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      }
    });
  };
  const quoteTag = (tag) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode(tag));
      }
    });
  };
  const formatCode = (language) => {
    // if (blockType !== 'code') {
    editor.update(() => {
      let selection = $getSelection();

      if (selection !== null) {
        if (selection.isCollapsed()) {
          const codeNode = $createCodeNode();
          codeNode.setLanguage(language);
          $setBlocksType(selection, () => codeNode);
        } else {
          const textContent = selection.getTextContent();
          const codeNode = $createCodeNode();
          codeNode.setLanguage(language);
          selection.insertNodes([codeNode]);
          selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertRawText(textContent);
          }
        }
      }
    });
    // }
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      // formatParagraph();
    }
  };

  const formatCheckList = () => {
    // if (blockType !== 'check') {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    // } else {
    //   formatParagraph();
    // }
  };

  const formatNumberedList = () => {
    // if (blockType !== 'number') {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    // } else {
    //   formatParagraph();
    // }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, $updateToolbar]);

  function sanitizeUrl(url) {
    try {
      const parsedUrl = new URL(url);
      // eslint-disable-next-line no-script-url
      if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
        return "about:blank";
      }
    } catch {
      return url;
    }
    return url;
  }

  const SUPPORTED_URL_PROTOCOLS = new Set([
    "http:",
    "https:",
    "mailto:",
    "sms:",
    "tel:",
  ]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === "KeyK" && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url;
          // if (!isLink) {
          setIsLinkEditMode(true);
          url = sanitizeUrl("https://");
          // } else {
          //   // setIsLinkEditMode(false);
          //   url = null;
          // }
          return editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  const insertLink = useCallback(() => {
    // if (!isLink) {
    setIsLinkEditMode(true);
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
    // } else {
    //   setIsLinkEditMode(false);
    //   editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    // }
  }, [editor]);
  console.log(blockType);
  return (
    <div className="toolbar" ref={toolbarRef}>
      <button
        disabled={!canUndo}
        className="edit-button"
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        aria-label="Undo"
      >
        <i className="fa fa-undo"></i>
      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="edit-button"
        aria-label="Redo"
      >
        <i className="fa fa-redo"></i>
      </button>
      <button
        disabled={isheadNode}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
        className={"edit-button" + (isBold ? " active" : "")}
        aria-label="Format Bold"
      >
        <i className="fa fa-bold"></i>
      </button>
      <button
        disabled={isheadNode}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
        className={"edit-button" + (isItalic ? " active" : "")}
        aria-label="Format Italics"
      >
        <i className="fa fa-italic"></i>
      </button>
      <button
        disabled={isheadNode}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
        }}
        className={"edit-button" + (isUnderline ? " active" : "")}
        aria-label="Format Underline"
      >
        <i className="fa fa-underline"></i>
      </button>

      <button
        onClick={() => {
          quoteTag();
        }}
        className={"edit-button"}
        aria-label="Format Underline"
      >
        <i className="fa fa-quote-left" aria-hidden="true"></i>
      </button>
      <DropDown
        icon={<i className="fa fa-heading"></i>}
        menu={[
          { name: "Heading 1", onClick: () => headingTag("h2") },
          { name: "Heading 2", onClick: () => headingTag("h3") },
          { name: "Heading 3", onClick: () => headingTag("h4") },
        ]}
      />

      <button
        onClick={() => {
          setImageInput(true);
        }}
        className="item"
      >
        <i className="fa fa-image"></i>
      </button>
      {imageInput && (
        <InsertImageDialog
          activeEditor={editor}
          onClose={() => {
            setImageInput(false);
          }}
        />
      )}

      <button
        onClick={() => {
          setYoutubeInput(true)
          // editor.dispatchCommand(INSERT_YOUTUBE_COMMAND, FillURL());
        }}
        className="item"
      >
        <i className="fa fa-icon fa-youtube"></i>
      </button>
      {youtubeInput && <YoutubeUrlDialog activeEditor={editor}
          onClose={() => {
            setYoutubeInput(false);
          }}/>}
      <DropDown
        icon={<i className="fa fa-solid fa-code"></i>}
        menu={[
          { name: "Python", onClick: () => formatCode("python") },
          { name: "Javascript", onClick: () => formatCode("javascript") },
          { name: "Shell", onClick: () => formatCode("shell") },

          { name: "Plain", onClick: () => formatCode("plain") },
        ]}
      />
      {/* <button
        onClick={formatCode}>
        <i class="fa fa-solid fa-code"></i>
      </button> */}

      <button onClick={formatBulletList}>
        <i className="fa fa-solid fa-list-ul"></i>
      </button>
      <button onClick={formatNumberedList}>
        <i className="fa fa-solid fa-list-ol"></i>
      </button>
      <button disabled={isheadNode} onClick={insertLink}>
        <i className="fa fa-solid fa-link"></i>
      </button>
    </div>
  );
}

const DropDown = ({ disabled, icon, menu }) => {
  return (
    <>
      <div className="">
        <button
          className="dropdown-toggle item"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          {icon}
        </button>
        <ul className="dropdown-menu">
          {menu.map((data) => {
            return (
              <li>
                <span className="dropdown-item" onClick={data.onClick}>
                  {data.name}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};
